import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Eye, EyeOff, Mail } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/types/supabase';

interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [isResetMode, setIsResetMode] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [isVerifyingToken, setIsVerifyingToken] = useState(true);

  // Check for password reset token in URL
  useEffect(() => {
    setIsVerifyingToken(true); // Start by assuming we might verify

    const tokenFromSearch = searchParams.get('token');
    const typeFromSearch = searchParams.get('type');

    const extractDataFromHash = () => {
      if (typeof window !== 'undefined') {
        const hash = window.location.hash;
        if (hash && (hash.includes('access_token=') || hash.includes('error_description='))) {
          const hashParams = new URLSearchParams(hash.substring(1));
          const errorDescription = hashParams.get('error_description');
          if (errorDescription) {
            return { error: decodeURIComponent(errorDescription), type: hashParams.get('type') };
          }
          return {
            accessToken: hashParams.get('access_token'),
            refreshToken: hashParams.get('refresh_token'),
            type: hashParams.get('type') // This is 'signup', 'recovery', etc.
          };
        }
      }
      return null;
    };

    const hashData = extractDataFromHash();

    let tokenToVerify: string | null = null;
    let associatedRefreshToken: string | undefined = undefined;

    if (hashData) {
      // Clean the hash as soon as we've read it, to prevent other logic/pages from misinterpreting it.
      if (window.location.hash) {
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      }

      if (hashData.error) {
        setError(`Error from authentication provider: ${hashData.error}`);
        setIsVerifyingToken(false);
        return; // Exit early on error
      }

      if (hashData.accessToken && hashData.type === 'recovery') {
        tokenToVerify = hashData.accessToken;
        associatedRefreshToken = hashData.refreshToken || undefined;
      } else if (hashData.accessToken && hashData.type && hashData.type !== 'recovery') {
        // Token from hash is present, but not for 'recovery' (e.g., 'signup', 'magiclink').
        // ResetPassword page should ignore it. Verify.tsx or other specific pages should handle it.
        console.log(`ResetPassword: Ignoring token from hash with type '${hashData.type}'.`);
        setIsVerifyingToken(false);
        return; // Exit early, do not process this token
      }
    }

    // If no token from hash was suitable (or no hash), check search parameters.
    if (!tokenToVerify && tokenFromSearch) {
      // Only process if type is 'recovery' or if type is not specified (for backward compatibility with older reset links).
      if (typeFromSearch === 'recovery' || !typeFromSearch) { 
        tokenToVerify = tokenFromSearch;
        // Typically, no refresh token from search params for this flow.
      } else if (typeFromSearch && typeFromSearch !== 'recovery') {
        // Token in search params, but explicitly for a different type.
        console.log(`ResetPassword: Ignoring token from search with type '${typeFromSearch}'.`);
        setIsVerifyingToken(false);
        return; // Exit early
      }
    }

    if (tokenToVerify) {
      verifyToken(tokenToVerify, associatedRefreshToken);
    } else {
      setIsVerifyingToken(false); // No suitable token found to verify.
    }
  }, [searchParams]); // navigate is not directly used in this hook but might be in verifyToken. Consider dependencies if verifyToken causes navigation.

  const verifyToken = async (token: string, refreshToken?: string) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Verifying token:', { 
        tokenType: token.includes('.') ? 'JWT' : 'hash',
        hasRefreshToken: !!refreshToken 
      });
      
      // For access_token format (from email links), we don't need to verify
      // Supabase already authenticated the user with this token
      if (token.includes('.')) {
        // This is likely a JWT token (has dots), so we can use it directly
        // Set the session using the token
        console.log('Setting session with JWT token');
        const { data, error } = await supabase.auth.setSession({
          access_token: token,
          refresh_token: refreshToken || '',
        });
        
        if (error) {
          console.error('Error setting session with JWT token:', error);
          throw error;
        }
        
        console.log('Session set successfully', { 
          hasSession: !!data.session,
          user: data.session?.user?.email || 'unknown'
        });
        
        setAccessToken(token);
        setIsResetMode(true);
      } else {
        // For other token formats, use verifyOtp
        console.log('Verifying OTP token');
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'recovery',
        });

        if (error) {
          console.error('Error verifying OTP token:', error);
          throw error;
        }
        
        console.log('OTP verified successfully', { 
          hasSession: !!data.session,
          user: data.session?.user?.email || 'unknown'
        });
        
        setAccessToken(token);
        setIsResetMode(true);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Invalid or expired reset link. Please request a new one. (${errorMessage})`);
      console.error('Token verification error:', err);
    } finally {
      setIsVerifyingToken(false);
      setLoading(false);
    }
  };

  // Validate password strength
  const validatePassword = (pwd: string): PasswordValidationResult => {
    const errors: string[] = [];
    if (pwd.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(pwd)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(pwd)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(pwd)) {
      errors.push('Password must contain at least one number');
    }
    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  // Handle password reset request
  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setSuccess('Password reset link sent to your email');
      toast({
        title: 'Success',
        description: 'Check your email for the password reset link',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send reset email';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle password update
  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const validation = validatePassword(password);
    if (!validation.isValid) {
      setPasswordErrors(validation.errors);
      setError('Please fix the password requirements');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.updateUser({
        password,
      });

      if (error) throw error;

      setSuccess('Password updated successfully');
      toast({
        title: 'Success',
        description: 'Your password has been updated. Please log in with your new password.',
      });
      
      // Sign out the user to prevent auto-login issues
      await supabase.auth.signOut();
      
      // Redirect to login after successful password reset
      setTimeout(() => {
        // Clear any hash or tokens from URL
        window.history.replaceState({}, document.title, '/login');
        navigate('/login');
      }, 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update password';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };



  if (isVerifyingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <Loader2 className="h-12 w-12 mx-auto animate-spin text-blue-600" />
          <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
            Verifying your reset link...
          </h2>
          {error && (
            <p className="mt-2 text-sm text-red-600">{error}</p>
          )}
        </div>
      </div>
    );
  }

  if (isResetMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Reset Your Password
            </h2>
          </div>
          
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <div className="text-red-700">{error}</div>
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
              <div className="text-green-700">{success}</div>
            </div>
          )}
          
          <form className="mt-8 space-y-6" onSubmit={handlePasswordUpdate}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div className="mb-4">
                <Label htmlFor="password" className="sr-only">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="New Password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordErrors([]);
                      setError('');
                    }}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              
              <div className="mb-4">
                <Label htmlFor="confirmPassword" className="sr-only">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError('');
                    }}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              
              {passwordErrors.length > 0 && (
                <div className="text-sm text-red-600 mb-4">
                  <p className="font-medium">Password must meet the following requirements:</p>
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    {passwordErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div>
              <Button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Updating...
                  </>
                ) : 'Update Password'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Initial reset password request form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Reset Your Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your email address and we'll send you a link to reset your password
          </p>
        </div>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
            <div className="text-red-700">{error}</div>
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
            <div className="text-green-700">{success}</div>
          </div>
        )}
        
        <form className="mt-8 space-y-6" onSubmit={handleResetRequest}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <Label htmlFor="email-address" className="sr-only">
                Email address
              </Label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none rounded-md relative block w-full px-3 py-2 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                />
              </div>
            </div>
          </div>

          <div>
            <Button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Sending...
                </>
              ) : 'Send Reset Link'}
            </Button>
          </div>
        </form>
        
        <div className="text-center">
          <Button
            variant="link"
            onClick={() => navigate('/login')}
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            Back to Sign in
          </Button>
        </div>
      </div>
    </div>
  );
}
